import {AlertTriangle, CheckCircle, Info, Trash2} from "lucide-react";
import {type ReactNode, useCallback, useEffect, useRef} from "react";
import {BaseButton} from "../BaseButton";

export type AlertVariant = "confirm" | "danger" | "success" | "warning" | "info";

export interface BaseAlertProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  variant?: AlertVariant;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
}

const iconMap: Record<AlertVariant, ReactNode> = {
  confirm: <Info size={28} />,
  danger: <Trash2 size={28} />,
  success: <CheckCircle size={28} />,
  warning: <AlertTriangle size={28} />,
  info: <Info size={28} />,
};

const iconBgMap: Record<AlertVariant, string> = {
  confirm: "bg-(--color-accent)/10 text-(--color-accent)",
  danger: "bg-(--color-danger)/10 text-(--color-danger)",
  success: "bg-(--color-success)/10 text-(--color-success)",
  warning: "bg-(--color-warning)/10 text-(--color-warning)",
  info: "bg-(--color-info)/10 text-(--color-info)",
};

const confirmButtonVariant: Record<AlertVariant, "primary" | "danger"> = {
  confirm: "primary",
  danger: "danger",
  success: "primary",
  warning: "primary",
  info: "primary",
};

export const BaseAlert = ({
  open,
  onClose,
  onConfirm,
  variant = "confirm",
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  loading = false,
}: BaseAlertProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    // Auto-focus the dialog for accessibility
    requestAnimationFrame(() => dialogRef.current?.focus());

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, onClose, loading]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current && !loading) onClose();
    },
    [onClose, loading]
  );

  if (!open) return null;

  const isAlertOnly = !onConfirm;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-fade-in"
    >
      <div
        ref={dialogRef}
        className="relative bg-(--color-bg-card) rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col items-center gap-4 text-center outline-none"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        {/* Icon */}
        <div className={["w-14 h-14 rounded-full flex items-center justify-center", iconBgMap[variant]].join(" ")}>
          {iconMap[variant]}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-(--color-text-primary)">{title}</h3>

        {/* Description */}
        {description && <p className="text-sm text-(--color-text-secondary) leading-relaxed">{description}</p>}

        {/* Buttons */}
        <div className="flex items-center gap-3 mt-2 w-full">
          {isAlertOnly ? (
            <BaseButton
              variant="primary"
              size="md"
              onClick={onClose}
              className="flex-1"
            >
              Aceptar
            </BaseButton>
          ) : (
            <>
              <BaseButton
                variant="secondary"
                size="md"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                {cancelLabel}
              </BaseButton>
              <BaseButton
                variant={confirmButtonVariant[variant]}
                size="md"
                onClick={onConfirm}
                loading={loading}
                className="flex-1"
              >
                {confirmLabel}
              </BaseButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
